import { Injectable } from '@angular/core';
import { getPermissions } from '@utils/permissionCollection';

import { IPermissions } from '../models/Configuration/IPermissions';

export interface IRoleSelectOption {
  name: string;
  value: number;
}

export interface IRoles {
  SESSION_ADMIN: 0;
  SESSION_MODERATOR: 1;
  SESSION_PARTICIPANT: 2;
  SESSION_EXTERNAL_EVALUATOR: 3;
}

export interface IStatuses {
  NOT_STARTED: 0;
  RUNNING: 1;
  PAUSED: 2;
  STOPPED: 3;
}

export interface IOrganizer {
  enabled: boolean;
  name: string;
  logo_id: string;
}

export interface IConstantConfig {
  userRoles: string[];
  rolesSelectOptions: IRoleSelectOption[];
  roles: IRoles;
  status: IStatuses;
  defaultOrganizer: IOrganizer;

  MAX_IDEAS: number;
}

export type IInputLengths = Record<string, number>;

@Injectable()
export class ConfigService {
  private currentConfig: IConstantConfig;
  private currentInputLengths: IInputLengths;
  private currentPermissions: IPermissions;

  constructor() {
    this.initConstantConfig();
    this.initPermissions();
  }

  private initPermissions() {
    this.currentPermissions = getPermissions(this);
  }

  private initConstantConfig() {
    this.currentConfig = {
      defaultOrganizer: { enabled: false, name: null, logo_id: null },
      userRoles: ['guest', 'user', 'client-admin', 'admin'],
      rolesSelectOptions: [
        { name: 'Admin', value: 0 },
        { name: 'Moderator', value: 1 },
        { name: 'Participant', value: 2 },
        { name: 'External Evaluator', value: 3 },
      ],
      roles: {
        SESSION_ADMIN: 0,
        SESSION_MODERATOR: 1,
        SESSION_PARTICIPANT: 2,
        SESSION_EXTERNAL_EVALUATOR: 3,
      },
      status: {
        NOT_STARTED: 0,
        RUNNING: 1,
        PAUSED: 2,
        STOPPED: 3,
      },
      MAX_IDEAS: 60,
    };

    this.currentInputLengths = {
      SESSION_DESCRIPTION: 10000,
      SESSION_ONE_LINER: 250,
      SESSION_TITLE: 45,
      TEAM_NAME: 60,
      EMAIL: 60,
      IDEA_DESCRIPTION: 15000,
      IDEA_ONE_LINER: 250,
      IDEA_TITLE: 45,
      BENEFIT: 105,
      CHALLENGE: 105,
      SOLUTION: 200,
      COMMENT: 1000,
      NOTIFICATIONS: 500,
      MORPHBOX_ELEMENT: 60,
      MORPHBOX_ROW: 60,
      MORPHBOX_NAME: 60,
      AXIS: 45,
      CHANNEL_NAME: 45,
      MESSAGE: 5000,
      CUSTOM_INSTRUCTION_NAME: 100,
      CUSTOM_INSTRUCTION_CONTENT: 5000,
    };
  }

  // Getters (so they are read-only)

  get userRoles() {
    return this.currentConfig.userRoles;
  }
  get rolesSelectOptions() {
    return this.currentConfig.rolesSelectOptions;
  }
  get roles() {
    return this.currentConfig.roles;
  }
  get status() {
    return this.currentConfig.status;
  }
  get MAX_IDEAS() {
    return this.currentConfig.MAX_IDEAS;
  }
  get inputLengths() {
    return this.currentInputLengths;
  }
  get permissions() {
    return this.currentPermissions;
  }
  get defaultOrganizer() {
    return Object.assign({}, this.currentConfig.defaultOrganizer);
  }
}
